/**
 * 霓虹蛇境 (Neon Snake Realm) - 游戏覆盖层组件
 *
 * 显示游戏开始前的提示界面和游戏结束后的得分总结界面。
 * 从 App.tsx 中提取的纯展示组件，无内部状态。
 *
 * 使用方式：
 * ```tsx
 * <GameOverlay
 *   status={gameState.status}
 *   score={gameState.score}
 *   onStart={() => dispatch({ type: 'START_GAME' })}
 * />
 * ```
 */

import React from 'react';
import { GameStatus } from '@/types/game';
import type { ScoreState } from '@/types/game';

// ============================================================================
// Props 类型定义
// ============================================================================

/**
 * GameOverlay 组件的 Props 接口。
 *
 * 组件根据 status 决定显示开始界面还是结束界面。
 * onStart 回调在用户点击"重新开始"按钮时触发。
 */
export interface GameOverlayProps {
  /** 当前游戏运行状态 */
  status: GameStatus;
  /** 当前得分状态（用于游戏结束界面展示） */
  score: ScoreState;
  /** 开始/重新开始游戏的回调函数 */
  onStart: () => void;
}

// ============================================================================
// GameOverlay 组件
// ============================================================================

/**
 * 游戏覆盖层组件。
 *
 * 根据游戏状态渲染两种不同的覆盖层：
 * 1. **Idle 状态**：显示游戏标题、副标题和操作提示
 * 2. **GameOver 状态**：显示得分总结、新纪录标识和重新开始按钮
 *
 * 其他状态（Playing、Paused）下不渲染任何内容。
 *
 * 组件为纯展示组件，所有交互通过 onStart 回调委托给父组件处理。
 * 所有样式通过全局 CSS 类名控制，确保视觉一致性。
 *
 * @param props - 组件属性
 * @returns 覆盖层的 JSX 元素，或 null（非 Idle/GameOver 状态时）
 */
const GameOverlay: React.FC<GameOverlayProps> = ({ status, score, onStart }) => {
  // ==========================================================================
  // 非覆盖层状态：不渲染任何内容
  // ==========================================================================

  if (status !== GameStatus.Idle && status !== GameStatus.GameOver) {
    return null;
  }

  // ==========================================================================
  // 判断是否为新纪录
  // ==========================================================================

  const isNewRecord =
    status === GameStatus.GameOver &&
    score.current >= score.highScore &&
    score.current > 0;

  // ==========================================================================
  // 渲染
  // ==========================================================================

  return (
    <div className="game-overlay">
      <div className="overlay-content">
        {/* ================================================================ */}
        {/* Idle 状态：开始界面 */}
        {/* ================================================================ */}
        {status === GameStatus.Idle && (
          <>
            <h2 className="overlay-title">霓虹蛇境</h2>
            <p className="overlay-subtitle">Neon Snake Realm</p>
            <div className="overlay-instructions">
              <p>按任意方向键开始游戏</p>
              <p className="overlay-hint">
                <kbd>↑↓←→</kbd> 或 <kbd>W A S D</kbd>
              </p>
            </div>
          </>
        )}

        {/* ================================================================ */}
        {/* GameOver 状态：结束界面 */}
        {/* ================================================================ */}
        {status === GameStatus.GameOver && (
          <>
            <h2 className="overlay-title game-over-title">游戏结束</h2>
            <div className="overlay-score-summary">
              <div className="final-score">
                <span className="final-score-label">本局得分</span>
                <span className="final-score-value">{score.current}</span>
              </div>
              <div className="final-high-score">
                <span className="final-score-label">最高分</span>
                <span className="final-score-value">{score.highScore}</span>
              </div>
              {isNewRecord && <div className="new-record">🎉 新纪录！</div>}
            </div>
            <button
              className="restart-button"
              onClick={onStart}
              autoFocus
            >
              重新开始
            </button>
            <p className="overlay-hint">或按任意方向键重新开始</p>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// 导出
// ============================================================================

export default GameOverlay;